import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { FileDropzone } from "./components/FileDropzone/FileDropzone";
import { selectStyles } from "../../../../pages/AdminPanelPage/components/ManageUserTab/types/SelectStyles";
import { CustomDatePicker } from "../../../../pages/HomePage/components/ListBlock/components/Filters/components/CustomDatePicker/CustomDatePicker";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchDefaultFilterData } from "./services/defaultFilters.service";
import { BsStars } from "react-icons/bs";
import { Tooltip } from "react-tooltip";
import { fetchRegulators } from "./services/fetchRegulators.service";
import { fetchFieldList } from "./services/fetchFieldList.service";
import { FaSpinner, FaTimes } from "react-icons/fa";
import { createDraftForm } from "./services/createDraftForm";
import { generateField } from "./services/generateField";
import { finalizeDraftForm } from "./services/finilizeDraftForm";
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { toast } from "react-toastify";
import { formatDate } from "../../../../pages/HomePage/helpers/formatDate";
import styles from "./AddNewRecordsModal.module.css";

interface IAddNewRecordsModalProps {
  setShowNewRecordsModal: React.Dispatch<React.SetStateAction<boolean>>;
  darkMode: boolean;
}

const AddNewRecordsModal: React.FC<IAddNewRecordsModalProps> = ({
  setShowNewRecordsModal,
  darkMode,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [regulator, setRegulator] = useState<any>(null);
  const [country, setCountry] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [classValue, setClassValue] = useState<any[]>([]);
  const [techProductValue, setTechProductValue] = useState<any[]>([]);
  const [techSectionValue, setTechSectionValue] = useState<any[]>([]);
  const [cityValue, setCityValue] = useState<any[]>([]);
  const [tagValue, setTagValue] = useState<any[]>([]);
  const [keywordValue, setKeywordValue] = useState<any[]>([]);
  const [date, setDate] = useState(() => new Date());
  const [dateIssue, setDateIssue] = useState(() => new Date());
  const [link, setLink] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [selectedRegulator, setSelectedRegulator] = useState<any>(null);
  // Step 2 fields:
  const [decision, setDecision] = useState("");
  const [shortSummary, setShortSummary] = useState("");
  const [summary, setSummary] = useState("");
  const [provisions, setProvisions] = useState("");
  const [participants, setParticipants] = useState("");
  const [publicationDateValue, setPublicationDateValue] = useState<
    [Date | any, Date | any]
  >([null, null]);
  const allowMultipleFiles = selectedRegulator?.value === "lotus_notes";
  const [filesTouched, setFilesTouched] = useState(false);
  const filesInvalid = !allowMultipleFiles && files.length === 0;
  const [linkTouched, setLinkTouched] = useState(false);
  const isValidHttpUrl = (s: string) => {
    try {
      const u = new URL(s.trim());
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };
  const linkInvalid =
    linkTouched && link.trim() !== "" && !isValidHttpUrl(link);
  const aiUpdateRef = useRef<AIField | null>(null);

  const isLotusNotes = selectedRegulator?.value === "lotus_notes";
  const cleanedSummary = summary?.replace(/<[^>]*>/g, "").trim() ?? "";
  const disableShortSummaryAI = cleanedSummary === "";

  const editorClassName = `${styles.editor} ${styles.markdownBlock} w-full text-secondary-color border border-b border-l border-r border-gray-300 dark:border-gray-300 bg-white dark:bg-gray-800 mb-2 min-h-[56px] max-h-[240px] overflow-y-auto leading-5 py-2 px-3 box-border`;

  const editorPlugins = [
    headingsPlugin(),
    listsPlugin(),
    thematicBreakPlugin(),
    quotePlugin(),
    tablePlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <div className="flex flex-wrap w-full text-xs">
          <UndoRedo /> <Separator />
          <BoldItalicUnderlineToggles />
          <BlockTypeSelect /> <Separator /> <ListsToggle />
          <Separator />
          <InsertTable /> <CreateLink />
        </div>
      ),
    }),
  ];
  type AIField =
    | "decision"
    | "short_summary"
    | "summary"
    | "provisions"
    | "participants";
  const [editorKeys, setEditorKeys] = useState<Record<AIField, number>>({
    decision: 0,
    short_summary: 0,
    summary: 0,
    provisions: 0,
    participants: 0,
  });
  const [isAIGenerated, setIsAIGenerated] = useState<Record<AIField, boolean>>({
    decision: false,
    short_summary: false,
    summary: false,
    provisions: false,
    participants: false,
  });

  const [aiLoading, setAiLoading] = useState<Record<AIField, boolean>>({
    decision: false,
    short_summary: false,
    summary: false,
    provisions: false,
    participants: false,
  });
  const { data: defaultFilterData, refetch } = useQuery({
    queryKey: ["defaultFilterData", selectedRegulator?.label || null],
    queryFn: () => {
      return fetchDefaultFilterData({
        regulator: selectedRegulator?.label,
      });
    },
    enabled: !!selectedRegulator,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: fieldList } = useQuery({
    queryKey: ["fieldList", selectedRegulator?.value || null],
    queryFn: () => {
      return fetchFieldList(selectedRegulator?.value || null);
    },
    enabled: !!selectedRegulator,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data: regulatorOptions } = useQuery({
    queryKey: ["regulatorOptions"],
    queryFn: fetchRegulators,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { mutate: mutateCreateDraftForm, isPending } = useMutation({
    mutationFn: async ({ form, files }: { form: any; files: File[] }) => {
      return await createDraftForm(form, files);
    },
    onSuccess: () => {
      setStep(3);
    },
    onError: (error: string) => {
      toast.error(error);
    },
  });

  const { mutate: mutateFinalizeDraftForm } = useMutation({
    mutationFn: async ({ form, files }: { form: any; files: File[] }) => {
      return await finalizeDraftForm(
        {
          ...form,
          date_issue: null,
          decision: decision ?? "",
          short_summary: shortSummary ?? "",
          summary: summary ?? "",
          provisions: provisions ?? "",
          participants: participants ?? "",
          regulator: selectedRegulator?.value,
          code: selectedRegulator?.value,
        },
        files,
        fieldList?.hidden_data?.id,
      );
    },
    onSuccess: () => {
      toast.success(t("newRecordModal.saveNewRecordToaster"));
      setShowNewRecordsModal(false);
    },
    onSettled: () => {
      // Notify other parts of the app that a new record was created so they can refresh
      try {
        window.dispatchEvent(new Event("records:created"));
      } catch (e) {
        // ignore
      }
    },
    onError: (error: Error) => {
      console.error("Error requesting a new Regulator:", error);
    },
  });

  const { mutate: mutateGenerateField, isPending: isGenPending } = useMutation({
    mutationFn: async ({ fieldName }: { fieldName: AIField }) => {
      return await generateField(
        fieldName,
        summary,
        fieldList?.hidden_data?.id,
        fieldList?.hidden_data?.code,
        fieldList?.hidden_data?.regulator,
      );
    },
    onMutate: ({ fieldName }) => {
      setAiLoading((s) => ({ ...s, [fieldName]: true }));
    },
    onSuccess: (data, { fieldName }) => {
      const pickText = (d: any, f: AIField): string =>
        typeof d === "string"
          ? d
          : (d?.[f] ??
            d?.content ??
            d?.text ??
            d?.result ??
            d?.data?.[f] ??
            "");

      const text = pickText(data, fieldName);
      aiUpdateRef.current = fieldName;

      switch (fieldName) {
        case "decision":
          setDecision(text);
          break;
        case "short_summary":
          setShortSummary(text);
          break;
        case "summary":
          setSummary(text);
          break;
        case "provisions":
          setProvisions(text);
          break;
        case "participants":
          setParticipants(text);
          break;
      }

      setEditorKeys((k) => ({ ...k, [fieldName]: (k[fieldName] ?? 0) + 1 }));
      setIsAIGenerated((prev) => ({ ...prev, [fieldName]: true }));
    },
    onError: (error: any, variables) => {
      toast.error(error?.message ?? "Error");

      const { fieldName } = variables;

      setIsAIGenerated((prev) => ({
        ...prev,
        [fieldName]: false,
      }));
    },
    onSettled: (_data, _err, { fieldName }) => {
      setAiLoading((s) => ({ ...s, [fieldName]: false }));
    },
  });

  useEffect(() => {
    if (selectedRegulator) refetch();
  }, [selectedRegulator, refetch]);

  useEffect(() => {
    setCountry(null);
  }, [selectedRegulator]);

  const handleNextFromRegulator = (e: any) => {
    e.preventDefault();
    if (selectedRegulator) setStep(2);
  };
  const handleNextFromDetails = (e: any) => {
    e.preventDefault();
    setFilesTouched(true);

    if (filesInvalid) return;

    const form: any = {
      classes: classValue,
      tech_product: techProductValue,
      tech_section_vat_act: techSectionValue,
      city: cityValue,
      tags: tagValue,
      keywords: keywordValue,
      date: formatDate(date),
      date_issue: formatDate(dateIssue),

      title,
      code: selectedRegulator?.value ?? null,
      link: link || null,

      country: fieldList?.hidden_data?.country ?? null,
      id: fieldList?.hidden_data?.id ?? null,
      regulator: fieldList?.hidden_data?.regulator ?? null,
    };

    mutateCreateDraftForm({ form, files: files });
  };
  const handleBackFromDetails = () => setStep(1);
  const handleBackFromAI = () => setStep(2);

  const getSelectStyles = (customStyles = {}) => ({
    ...selectStyles(darkMode),
    input: (base: any) => ({
      ...base,
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    option: (base: any, { isFocused }: any) => ({
      ...base,
      backgroundColor: isFocused ? "#86BC25" : darkMode ? "#374151" : "#FFFFFF",
      color: darkMode ? "#FFFFFF" : "#000000",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: darkMode ? "#43B02A" : "#26890D",
    }),
    multiValueLabel: (base: any) => ({ ...base, color: "#e2e8f0" }),
    ...customStyles,
  });

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    if (dates[0] && dates[1] && dates[0].getTime() === dates[1].getTime()) {
      dates[1] = null;
    }
    setPublicationDateValue(dates);
  };

  useEffect(() => {
    if (regulator && regulator.value) refetch();
  }, [regulator?.value]);

  useEffect(() => {
    if (country) {
      setRegulator(null);
      refetch();
    }
  }, [country]);

  const generateAIContent = (field: AIField) => {
    if (aiLoading[field]) return;
    mutateGenerateField({ fieldName: field });
  };

  const buildFinalizeForm = () => ({
    classes: classValue,
    tech_product: techProductValue,
    tech_section_vat_act: techSectionValue,
    city: cityValue,
    tags: tagValue,
    keywords: keywordValue,

    title,
    code: selectedRegulator?.value ?? null,
    date: date ? new Date(date).toISOString() : null,
    link: link || null,

    country: fieldList?.hidden_data?.country ?? null,
    id: fieldList?.hidden_data?.id ?? null,
  });

  function handleFinalizeSubmit(e: React.FormEvent) {
    e.preventDefault();

    const baseForm = buildFinalizeForm();

    mutateFinalizeDraftForm({ form: baseForm, files });
  }

  useEffect(() => {
    if (files.length > 0 && filesTouched) setFilesTouched(false);
  }, [files, filesTouched]);

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const handleFieldChange = (field: AIField, value: string) => {
    switch (field) {
      case "decision":
        setDecision(value);
        break;
      case "short_summary":
        setShortSummary(value);
        break;
      case "summary":
        setSummary(value);
        break;
      case "provisions":
        setProvisions(value);
        break;
      case "participants":
        setParticipants(value);
        break;
    }

    if (aiUpdateRef.current === field) {
      aiUpdateRef.current = null;
      return;
    }

    if (isAIGenerated[field]) {
      setIsAIGenerated((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  return (
    <div className="modal-overlay px-8 py-4">
      <div className="flex gap-8 w-full min-w-[500px] items-start justify-center text-sm text-gray-400 dark:text-gray-300">
        <div className="modal-panel p-4 w-[700px] self-center">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl title-base-color text-center flex-1">
              {t("newRecordModal.addNewRecordTitle")}
            </h2>
            <button
              aria-label="Close"
              className="ml-2 -mt-3 -mr-3 modal-close-btn"
              onClick={() => setShowNewRecordsModal(false)}
              type="button"
            >
              <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
            </button>
          </div>

          <div className="flex justify-center">
            <span className="px-3 py-1 rounded-full bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 text-xs font-semibold">
              {step === 1 && t("newRecordModal.step1")}
              {step === 2 && t("newRecordModal.step2")}
              {step === 3 && t("newRecordModal.step3")}
            </span>
          </div>
          {/* Step 1: Regulator */}
          {step === 1 && (
            <form onSubmit={handleNextFromRegulator}>
              <div className="mb-3 mt-3">
                <label className="block text-base title-base-color mb-1">
                  {t("docDetails.regulator")}{" "}
                  <span className="text-del-red">*</span>
                </label>
                <Select
                  isSearchable
                  value={selectedRegulator}
                  options={regulatorOptions
                    ?.map((r: any) => ({
                      value: r.code,
                      label: r.name,
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label))}
                  onChange={setSelectedRegulator}
                  required
                  classNamePrefix="react-select"
                  placeholder={t("userSettings.selectRegulator")}
                  styles={getSelectStyles()}
                />
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewRecordsModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-black"
                >
                  {t("adminPanel.cancel")}
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-sm ${!selectedRegulator ? "btn-disabled" : "btn-green"}`}
                  disabled={!selectedRegulator}
                >
                  {t("pagination.next")}
                </button>
              </div>
            </form>
          )}
          {step === 2 && (
            <form
              onSubmit={handleNextFromDetails}
              className="flex flex-col max-h-[70vh]"
            >
              {isPending ? (
                <div className="flex flex-col items-center justify-center py-12 min-h-[40vh]">
                  <FaSpinner
                    className="animate-spin mb-3"
                    size={24}
                    color={darkMode ? "#FFFFFF" : "#000000"}
                  />
                  <span
                    className={darkMode ? "text-gray-200" : "text-gray-700"}
                  >
                    {t("newRecordModal.saveDraft")}
                  </span>
                </div>
              ) : (
                <>
                  <div className="overflow-y-auto px-2 -mr-2">
                    {/* Regulator select */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("docDetails.regulator")}{" "}
                        <span className="text-del-red">*</span>
                      </label>
                      <Select
                        value={{
                          value: selectedRegulator?.value,
                          label: selectedRegulator?.label,
                        }}
                        isDisabled
                        classNamePrefix="react-select"
                        styles={selectStyles(darkMode, true)}
                      />
                    </div>

                    {/* Case name (unique) */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("docDetails.caseName")}
                        <span className="text-del-red">*</span>
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        placeholder={t("newRecordModal.caseNamePlaceholder")}
                        className={`w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-2 ${
                          darkMode
                            ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                            : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
                        }`}
                      />
                    </div>

                    {/* Class */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("docDetails.class")}
                        <span className="text-del-red">*</span>
                      </label>
                      <CreatableSelect
                        isMulti
                        isSearchable
                        value={classValue}
                        options={defaultFilterData?.classes
                          ?.map((c: string) => ({ value: c, label: c }))
                          .sort((a: any, b: any) =>
                            a.label.localeCompare(b.label),
                          )}
                        onChange={(v) => setClassValue(v as any)}
                        required
                        classNamePrefix="react-select"
                        placeholder={t("filtersSection.searchAndSelectClass")}
                        styles={getSelectStyles()}
                      />
                    </div>

                    {/* Date */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("newRecordModal.date")}{" "}
                        <span className="text-del-red">*</span>
                      </label>
                      <CustomDatePicker
                        startDate={date}
                        endDate={publicationDateValue[1]}
                        onChange={(date: Date) => setDate(date)}
                        onClear={() => handleDateChange([null, null])}
                        isRange={false}
                      />
                    </div>

                    {/* Date of issue*/}
                    {defaultFilterData?.date_issue && (
                      <div className="mb-3">
                        <label className="block text-base title-base-color mb-1">
                          {t("filtersSection.dateOfIssue")}
                          <span className="text-del-red">*</span>
                        </label>
                        <CustomDatePicker
                          startDate={dateIssue}
                          endDate={publicationDateValue[1]}
                          onChange={(date: Date) => setDateIssue(date)}
                          onClear={() => handleDateChange([null, null])}
                          isRange={false}
                        />
                      </div>
                    )}

                    {/* Tech Product */}
                    {defaultFilterData?.tech_product && (
                      <div className="mb-3">
                        <label className="block text-base title-base-color mb-1">
                          {t("filtersSection.techProduct")}
                        </label>
                        <CreatableSelect
                          isMulti
                          isSearchable
                          value={techProductValue}
                          options={defaultFilterData?.tech_product
                            ?.map((c: string) => ({ value: c, label: c }))
                            .sort((a: any, b: any) =>
                              a.label.localeCompare(b.label),
                            )}
                          onChange={(v) => setTechProductValue(v as any)}
                          classNamePrefix="react-select"
                          placeholder={t(
                            "filtersSection.searchAndSelectTechProduct",
                          )}
                          styles={getSelectStyles()}
                        />
                      </div>
                    )}

                    {/* Tech Section VAT Act */}
                    {defaultFilterData?.tech_section_vat_act && (
                      <div className="mb-3">
                        <label className="block text-base title-base-color mb-1">
                          {t("filtersSection.techSectionVATAct")}
                        </label>
                        <CreatableSelect
                          isMulti
                          isSearchable
                          value={techSectionValue}
                          options={defaultFilterData?.tech_section_vat_act
                            ?.map((c: string) => ({ value: c, label: c }))
                            .sort((a: any, b: any) =>
                              a.label.localeCompare(b.label),
                            )}
                          onChange={(v) => setTechSectionValue(v as any)}
                          classNamePrefix="react-select"
                          placeholder={t(
                            "filtersSection.searchAndSelectTechSection",
                          )}
                          styles={getSelectStyles()}
                        />
                      </div>
                    )}

                    {/* City */}
                    {defaultFilterData?.city && (
                      <div className="mb-3">
                        <label className="block text-base title-base-color mb-1">
                          {t("filtersSection.city")}
                        </label>
                        <CreatableSelect
                          isMulti
                          isSearchable
                          value={cityValue}
                          options={defaultFilterData?.city
                            ?.map((c: string) => ({ value: c, label: c }))
                            .sort((a: any, b: any) =>
                              a.label.localeCompare(b.label),
                            )}
                          onChange={(v) => setCityValue(v as any)}
                          classNamePrefix="react-select"
                          placeholder={t("filtersSection.searchAndSelectCity")}
                          styles={getSelectStyles()}
                        />
                      </div>
                    )}

                    {/* Tags */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("newRecordModal.tags")}
                      </label>
                      <CreatableSelect
                        isMulti
                        isSearchable
                        value={tagValue}
                        options={defaultFilterData?.tags
                          ?.map((c: string) => ({ value: c, label: c }))
                          .sort((a: any, b: any) =>
                            a.label.localeCompare(b.label),
                          )}
                        onChange={(v) => setTagValue(v as any)}
                        classNamePrefix="react-select"
                        placeholder={t("filtersSection.searchAndSelectTags")}
                        styles={getSelectStyles()}
                      />
                    </div>

                    {/* Keywords */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("docDetails.keywords")}
                      </label>
                      <CreatableSelect
                        isMulti
                        isSearchable
                        value={keywordValue}
                        options={defaultFilterData?.keywords
                          ?.map((c: string) => ({ value: c, label: c }))
                          .sort((a: any, b: any) =>
                            a.label.localeCompare(b.label),
                          )}
                        onChange={(v) => setKeywordValue(v as any)}
                        classNamePrefix="react-select"
                        placeholder={t(
                          "filtersSection.searchAndSelectKeywords",
                        )}
                        styles={getSelectStyles()}
                      />
                    </div>

                    {/* File Upload */}
                    <div className="mb-1">
                      <label className="block text-base title-base-color mb-1">
                        {allowMultipleFiles
                          ? t("adminPanel.files")
                          : t("adminPanel.file")}
                        {!allowMultipleFiles && (
                          <span className="text-del-red">*</span>
                        )}
                      </label>
                      <FileDropzone
                        allowMultipleFiles={allowMultipleFiles}
                        setFiles={setFiles}
                        files={files}
                      />
                      {filesInvalid && filesTouched && (
                        <p className="mt-1 text-xs text-del-red">
                          {t("newRecordModal.fileRequiredError")}
                        </p>
                      )}
                    </div>
                    {/* Link */}
                    <div className="mb-3">
                      <label className="block text-base title-base-color mb-1">
                        {t("docDetails.link")}
                      </label>
                      <input
                        type="url"
                        inputMode="url"
                        autoComplete="url"
                        value={link}
                        onChange={(e) => {
                          setLink(e.target.value);
                        }}
                        onBlur={() => setLinkTouched(true)}
                        placeholder="https://example.com/page"
                        aria-invalid={linkInvalid}
                        className={`w-full px-3 py-1.5 border rounded focus:outline-none focus:ring-2 ${
                          linkInvalid
                            ? "border-red-500 focus:ring-red-500"
                            : darkMode
                              ? "border-gray-600 focus:ring-green-500"
                              : "border-gray-300 focus:ring-green-400"
                        }
                    ${
                      darkMode
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-200 text-gray-700"
                    }
                    }`}
                      />
                      {linkInvalid && (
                        <p className="mt-1 text-xs text-del-red">
                          {t("newRecordModal.linkPlaceholder")}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={handleBackFromDetails}
                      className="px-4 py-2 text-sm bg-gray-300 rounded hover:bg-gray-400 text-black"
                    >
                      {t("newRecordModal.back")}
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 text-sm ${
                        title !== "" &&
                        classValue.length > 0 &&
                        !filesInvalid &&
                        !linkInvalid
                          ? "btn-green"
                          : "btn-disabled"
                      }`}
                      disabled={
                        !(
                          title !== "" &&
                          classValue.length > 0 &&
                          !filesInvalid &&
                          !linkInvalid
                        )
                      }
                    >
                      {t("pagination.next")}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
          {step === 3 && (
            // Step 3: Decision, Short Summary, Summary
            <form
              onSubmit={handleFinalizeSubmit}
              className="flex flex-col max-h-[70vh]"
            >
              <div className="overflow-y-auto px-2 -mr-2 min-h-0">
                <div className="mb-4">
                  <label className="flex items-center justify-between title-base-color mb-1">
                    {selectedRegulator.value !== "lotus_notes" ? (
                      <span className="text-base">
                        {t("docDetails.summary")}
                      </span>
                    ) : (
                      <span className="text-base">{t("docDetails.text")}</span>
                    )}
                    {selectedRegulator.value !== "lotus_notes" && (
                      <button
                        type="button"
                        className="ml-2 pr-1 rounded text-gray-600 dark:text-white"
                      >
                        <BsStars
                          onClick={() => generateAIContent("summary")}
                          className={`${isAIGenerated.summary ? "text-del-green" : "hover:text-del-green"} summary-ai-generate-tooltip w-4 h-4`}
                        />
                      </button>
                    )}
                  </label>
                  <Tooltip
                    anchorSelect={".summary-ai-generate-tooltip"}
                    place="top"
                    className="text-xs font-light z-50"
                    content={t("newRecordModal.generateAIField")}
                  />
                  {aiLoading.summary ? (
                    <div className="text-center p-4">
                      <FaSpinner
                        className="animate-spin inline-block mr-2"
                        size={24}
                        color={darkMode ? "#FFFFFF" : "#000000"}
                      />
                    </div>
                  ) : (
                    <MDXEditor
                      key={editorKeys.summary}
                      markdown={summary}
                      onChange={(value) => handleFieldChange("summary", value)}
                      contentEditableClassName={editorClassName}
                      plugins={editorPlugins}
                    />
                  )}
                </div>

                <div className="mb-4">
                  <label className="flex items-center justify-between title-base-color mb-1">
                    <span className="text-base">
                      {t("newRecordModal.shortSummary")}
                    </span>
                    <button
                      type="button"
                      className="ml-2 pr-1 rounded text-gray-600 dark:text-white"
                    >
                      <BsStars
                        onClick={() => {
                          if (!disableShortSummaryAI) {
                            generateAIContent("short_summary");
                          }
                        }}
                        className={`${isAIGenerated.short_summary ? "text-del-green" : "hover:text-del-green"} ${
                          disableShortSummaryAI
                            ? "opacity-80 cursor-not-allowed"
                            : ""
                        } shortSummary-ai-generate-tooltip w-4 h-4`}
                      />
                    </button>
                  </label>
                  <Tooltip
                    anchorSelect={".shortSummary-ai-generate-tooltip"}
                    place="top"
                    className="text-xs font-light z-50"
                    content={
                      disableShortSummaryAI
                        ? isLotusNotes
                          ? t("newRecordModal.shortSummaryTextTooltip")
                          : t("newRecordModal.shortSummaryTooltip")
                        : t("newRecordModal.generateAIField")
                    }
                  />
                  {aiLoading.short_summary ? (
                    <div className="text-center p-4">
                      <FaSpinner
                        className="animate-spin inline-block mr-2"
                        size={24}
                        color={darkMode ? "#FFFFFF" : "#000000"}
                      />
                    </div>
                  ) : (
                    <MDXEditor
                      key={editorKeys.short_summary}
                      markdown={shortSummary}
                      onChange={(value) =>
                        handleFieldChange("short_summary", value)
                      }
                      contentEditableClassName={editorClassName}
                      plugins={editorPlugins}
                    />
                  )}
                </div>

                {selectedRegulator.value !== "lotus_notes" && (
                  <div className="mb-4 text-sm">
                    <label className="flex items-center justify-between title-base-color mb-1">
                      <span className="text-base">
                        {t("docDetails.decision")}
                      </span>
                      <button
                        type="button"
                        className="pr-1 rounded text-gray-600 dark:text-white"
                      >
                        <BsStars
                          onClick={() => generateAIContent("decision")}
                          className={`decision-ai-generate-tooltip w-4 h-4 ${
                            isAIGenerated.decision
                              ? "text-del-green"
                              : "hover:text-del-green"
                          } ${aiLoading.decision ? "animate-pulse opacity-70 pointer-events-none" : ""}`}
                        />
                      </button>
                    </label>
                    <Tooltip
                      anchorSelect={".decision-ai-generate-tooltip"}
                      place="top"
                      className="text-xs font-light z-50"
                      content={t("newRecordModal.generateAIField")}
                    />
                    {aiLoading.decision ? (
                      <div className="text-center p-4">
                        <FaSpinner
                          className="animate-spin inline-block mr-2"
                          size={24}
                          color={darkMode ? "#FFFFFF" : "#000000"}
                        />
                      </div>
                    ) : (
                      <MDXEditor
                        key={editorKeys.decision}
                        markdown={decision}
                        onChange={(value) =>
                          handleFieldChange("decision", value)
                        }
                        contentEditableClassName={editorClassName}
                        plugins={editorPlugins}
                      />
                    )}
                  </div>
                )}

                {selectedRegulator.value !== "lotus_notes" && (
                  <div className="mb-4">
                    <label className="flex items-center justify-between title-base-color mb-1">
                      <span className="text-base">
                        {t("docDetails.participants")}
                      </span>
                      <button
                        type="button"
                        className="ml-2 pr-1 rounded text-gray-600 dark:text-white"
                      >
                        <BsStars
                          onClick={() => generateAIContent("participants")}
                          className={`${isAIGenerated.participants ? "text-del-green" : "hover:text-del-green"} participant-ai-generate-tooltip w-4 h-4`}
                        />
                      </button>
                    </label>
                    <Tooltip
                      anchorSelect={".participants-ai-generate-tooltip"}
                      place="top"
                      className="text-xs font-light z-50"
                      content={t("newRecordModal.generateAIField")}
                    />
                    {aiLoading.participants ? (
                      <div className="text-center p-4">
                        <FaSpinner
                          className="animate-spin inline-block mr-2"
                          size={24}
                          color={darkMode ? "#FFFFFF" : "#000000"}
                        />
                      </div>
                    ) : (
                      <MDXEditor
                        key={editorKeys.participants}
                        markdown={participants}
                        onChange={(value) =>
                          handleFieldChange("participants", value)
                        }
                        contentEditableClassName={editorClassName}
                        plugins={editorPlugins}
                      />
                    )}
                  </div>
                )}
                {selectedRegulator.value !== "lotus_notes" && (
                  <div className="mb-4">
                    <label className="flex items-center justify-between title-base-color mb-1">
                      <span className="text-base">
                        {t("docDetails.provisions")}
                      </span>
                      <button
                        type="button"
                        className="ml-2 pr-1 rounded text-gray-600 dark:text-white"
                      >
                        <BsStars
                          onClick={() => generateAIContent("provisions")}
                          className={`${isAIGenerated.provisions ? "text-del-green" : "hover:text-del-green"} provisions-ai-generate-tooltip w-4 h-4`}
                        />
                      </button>
                    </label>
                    <Tooltip
                      anchorSelect={".provisions-ai-generate-tooltip"}
                      place="top"
                      className="text-xs font-light z-50"
                      content={t("newRecordModal.generateAIField")}
                    />
                    {aiLoading.provisions ? (
                      <div className="text-center p-4">
                        <FaSpinner
                          className="animate-spin inline-block mr-2"
                          size={24}
                          color={darkMode ? "#FFFFFF" : "#000000"}
                        />
                      </div>
                    ) : (
                      <MDXEditor
                        key={editorKeys.provisions}
                        markdown={provisions}
                        onChange={(value) =>
                          handleFieldChange("provisions", value)
                        }
                        contentEditableClassName={editorClassName}
                        plugins={editorPlugins}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between space-x-4 mt-2">
                <button
                  type="button"
                  onClick={handleBackFromAI}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-black text-sm"
                >
                  {t("newRecordModal.back")}
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-sm ${
                    Object.values(aiLoading).some(Boolean) || isGenPending
                      ? "btn-disabled"
                      : "btn-green"
                  }`}
                  disabled={Object.values(aiLoading).some(Boolean) || isGenPending}
                >
                  {t("adminPanel.submit")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export { AddNewRecordsModal };
